import { EyeIcon, EyeSlashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { forwardRef, useEffect, useState } from 'react';
import { cn } from '@/shared/lib/utils';

const FULL_WIDTH_CHAR_MAP: Record<string, string> = {
  '０': '0',
  '１': '1',
  '２': '2',
  '３': '3',
  '４': '4',
  '５': '5',
  '６': '6',
  '７': '7',
  '８': '8',
  '９': '9',
  '．': '.',
  '－': '-',
  '＋': '+',
  '，': ',',
};

const normalizeNumericCharacters = (value: string) =>
  value.replace(/[０-９．－＋，]/g, (char) => FULL_WIDTH_CHAR_MAP[char] ?? char);

const extractNumericValue = (value: string) =>
  normalizeNumericCharacters(value)
    // カンマは内部値から除去
    .replace(/,/g, '');

const createNumericChangeEvent = (
  event: React.ChangeEvent<HTMLInputElement>,
  nextValue: string,
) => {
  const numericValue = nextValue === '' ? Number.NaN : Number(nextValue);

  return {
    ...event,
    target: {
      ...event.target,
      value: nextValue,
      valueAsNumber: numericValue,
    },
    currentTarget: {
      ...event.currentTarget,
      value: nextValue,
      valueAsNumber: numericValue,
    },
  } as React.ChangeEvent<HTMLInputElement>;
};

type InputProps = Omit<React.ComponentProps<'input'>, 'size' | 'prefix' | 'autoComplete'> & {
  /// 自動補完の設定（true: "on", false: "off", 文字列: そのまま渡す）
  autoComplete: boolean | string;
  /**
   * カンマ区切りで数値を表示するか
   * type="number"の場合に有効
   */
  useCommaFormat?: boolean;
  /**
   * パスワードの表示/非表示切り替えボタンを表示するか
   * type="password"の場合に有効（デフォルト: true）
   */
  showPasswordToggle?: boolean;
  /**
   * 入力フィールドの右側に表示する接尾辞（例: "千円", "円", "km"）
   */
  suffix?: React.ReactNode;
  /**
   * 入力フィールドの左側に表示する接頭辞（例: "¥", "$", アイコン）
   */
  prefix?: React.ReactNode;
  /**
   * クリアボタンを表示するか（値が入力されている場合のみ表示）
   */
  showAllClear?: boolean;
};

/**
 * Input コンポーネント
 *
 * 基本的なテキスト入力フィールド
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      onKeyDown,
      onChange,
      onBlur,
      min,
      useCommaFormat,
      value,
      inputMode: inputModeProp,
      showPasswordToggle = true,
      suffix,
      prefix,
      showAllClear = false,
      autoComplete,
      ...props
    },
    ref,
  ) => {
    const [displayValue, setDisplayValue] = useState<string>('');
    const [showPassword, setShowPassword] = useState(false);
    const isNumberWithComma = type === 'number' && useCommaFormat;
    const isNumericType = type === 'number';
    const isPasswordType = type === 'password';
    const hasSuffix = Boolean(suffix);
    const hasPrefix = Boolean(prefix);
    const hasValue = isNumberWithComma
      ? Boolean(displayValue)
      : Boolean(value != null && value !== '');
    const shouldShowClearButton = showAllClear && hasValue && !props.disabled && !props.readOnly;

    // 初期値の設定
    useEffect(() => {
      if (isNumberWithComma && value !== undefined && value !== '') {
        const normalizedValue = extractNumericValue(
          typeof value === 'string' ? value : String(value ?? ''),
        );
        const numValue = normalizedValue;
        if (numValue && !Number.isNaN(Number(numValue))) {
          setDisplayValue(Number(numValue).toLocaleString('ja-JP'));
        } else {
          setDisplayValue('');
        }
      }
    }, [value, isNumberWithComma]);

    // type="number"かつmin>=0の場合、マイナス記号の入力を禁止
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (isNumericType && min !== undefined && Number(min) >= 0) {
        // マイナス記号（-）、プラス記号（+）、e（指数表記）を禁止
        if (e.key === '-' || e.key === '+' || e.key === 'e' || e.key === 'E') {
          e.preventDefault();
        }
      }

      // 頭に0が入力されるのを防ぐ
      if (isNumericType) {
        const target = e.currentTarget;
        const currentValue = isNumberWithComma ? displayValue.replace(/,/g, '') : target.value;
        const selectionStart = target.selectionStart ?? 0;

        // 最初の文字が0で、さらに数字を入力しようとしている場合（小数点以外）
        if (
          e.key >= '0' &&
          e.key <= '9' &&
          currentValue === '0' &&
          selectionStart === 1 &&
          e.key !== '.'
        ) {
          e.preventDefault();
        }
      }

      onKeyDown?.(e);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (isNumberWithComma) {
        const rawValue = extractNumericValue(e.target.value);

        // 数値として有効な場合のみ処理
        if (rawValue === '' || !Number.isNaN(Number(rawValue))) {
          // 表示用にカンマ付き文字列を保持
          setDisplayValue(rawValue ? Number(rawValue).toLocaleString('ja-JP') : '');

          // React Hook Formには数値として渡す
          onChange?.(createNumericChangeEvent(e, rawValue));
        }
      } else if (isNumericType) {
        const pureValue = extractNumericValue(e.target.value);
        if (e.currentTarget.value !== pureValue) {
          e.currentTarget.value = pureValue;
        }
        onChange?.(createNumericChangeEvent(e, pureValue));
      } else {
        onChange?.(e);
      }
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      if (isNumberWithComma) {
        const rawValue = extractNumericValue(displayValue);
        if (rawValue) {
          setDisplayValue(Number(rawValue).toLocaleString('ja-JP'));
        }
      }
      onBlur?.(e);
    };

    const handleClear = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();

      if (isNumberWithComma) {
        setDisplayValue('');
      }

      // React Hook FormのonChangeイベントを生成してクリアを通知
      const syntheticEvent = {
        target: { value: '', valueAsNumber: Number.NaN },
        currentTarget: { value: '', valueAsNumber: Number.NaN },
      } as React.ChangeEvent<HTMLInputElement>;

      onChange?.(syntheticEvent);
    };

    const inputType = isNumberWithComma ? 'text' : isPasswordType && showPassword ? 'text' : type;

    const inputElement = (
      <input
        type={inputType}
        className={cn(
          'input-field',
          'flex h-8 w-full',
          isPasswordType && showPasswordToggle && 'pr-10',
          hasSuffix && 'pr-16',
          showAllClear && 'pr-10',
          hasPrefix && 'pl-9',
          className,
        )}
        ref={ref}
        min={min}
        value={isNumberWithComma ? displayValue : (value ?? '')}
        onKeyDown={handleKeyDown}
        onChange={handleChange}
        onBlur={handleBlur}
        inputMode={
          inputModeProp ?? (isNumberWithComma ? 'numeric' : isNumericType ? 'decimal' : undefined)
        }
        autoComplete={typeof autoComplete === 'string' ? autoComplete : autoComplete ? 'on' : 'off'}
        {...props}
      />
    );

    // prefix, suffix, passwordToggle, または clearButton がある場合はラッパーで囲む
    if (hasPrefix || hasSuffix || (isPasswordType && showPasswordToggle) || showAllClear) {
      return (
        <div className="relative w-full">
          {hasPrefix && (
            <div className="pointer-events-none absolute top-1/2 left-3 flex -translate-y-1/2 items-center text-text-muted">
              {prefix}
            </div>
          )}
          {inputElement}
          {hasSuffix && (
            <div className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-sm text-text-muted">
              {suffix}
            </div>
          )}
          {shouldShowClearButton && (
            <button
              type="button"
              onClick={handleClear}
              className="absolute top-1/2 right-3 -translate-y-1/2 cursor-pointer text-text-muted transition-colors hover:text-foreground"
              aria-label="入力をクリア"
              tabIndex={-1}
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          )}
          {isPasswordType && showPasswordToggle && (
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute top-1/2 right-3 -translate-y-1/2 text-text-muted transition-colors hover:text-foreground"
              aria-label={showPassword ? '入力を隠す' : '入力を表示'}
              tabIndex={-1}
            >
              {showPassword ? (
                <EyeSlashIcon className="h-4 w-4" />
              ) : (
                <EyeIcon className="h-4 w-4" />
              )}
            </button>
          )}
        </div>
      );
    }

    return inputElement;
  },
);

Input.displayName = 'Input';
